import { UseFormReturn } from "react-hook-form";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Plan } from "@/types/models";
import { DollarSign, Clock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { getCurrentExchangeRate } from "@/integrations/supabase/services";
import { RentalFormValues } from "@/hooks/useRentalForm";

interface PlanSelectionFormProps {
  form: UseFormReturn<RentalFormValues>;
  plans: Plan[];
  selectedPlan: Plan | null;
  onPlanChange: (planId: string) => void;
  durationHours: number;
  onDurationChange: (hours: number) => void;
  totalAmount: number;
  expectedReturnTime: Date | null;
}

const getPlanDurationInHours = (plan: Plan) => {
  let planDurationInHours = 0;
  if (plan && plan.time_duration.includes('min')) {
    const minutes = parseInt(plan.time_duration);
    planDurationInHours = minutes / 60;
  } else if (plan && plan.time_duration.includes('hora')) {
    planDurationInHours = parseInt(plan.time_duration);
  }
  return planDurationInHours;
}

const PlanSelectionForm = ({
  form,
  plans,
  selectedPlan,
  onPlanChange,
  durationHours,
  onDurationChange,
  totalAmount,
  expectedReturnTime,
}: PlanSelectionFormProps) => {
  const activePlans = plans.filter(plan => plan.active);

  const { data: exchangeRate, isLoading: isLoadingExchangeRate } = useQuery({
    queryKey: ['current-exchange-rate'],
    queryFn: getCurrentExchangeRate,
  });

  return (
    <div className="space-y-4">
      <div className="border-b pb-4">
        <h3 className="text-lg font-semibold">Plan de Alquiler</h3>
        <p className="text-sm text-gray-600">Selecciona el plan y duración del alquiler</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="planId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Plan</FormLabel>
                <Select onValueChange={(value) => {
                  field.onChange(value);
                  onPlanChange(value);
                }}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona un plan" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {activePlans.map((plan) => (
                      <SelectItem key={plan.id} value={plan.id}>
                        <div className="flex items-center justify-between w-full">
                          <span>{plan.name}</span>
                          <div className="flex items-center ml-2">
                            <DollarSign className="h-3 w-3" />
                            <span className="text-sm">{plan.price}/{plan.time_duration}</span>
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="durationHours"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Duración (horas)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.5"
                    min="0.5"
                    placeholder="1.5"
                    {...field}
                    value={selectedPlan && selectedPlan.extend ? field.value : getPlanDurationInHours(selectedPlan)}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      field.onChange(value);
                      onDurationChange(value);
                    }}
                    disabled={selectedPlan && !selectedPlan.extend}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {selectedPlan && durationHours > 0 && (
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Plan seleccionado:</span>
                  <span className="font-medium">{selectedPlan.name}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Precio por {selectedPlan.time_duration}:</span>
                  <div className="flex items-center">
                    <DollarSign className="h-4 w-4" />
                    <span className="font-medium">{selectedPlan.price}</span>
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Duración:</span>
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    <span className="font-medium">{durationHours} horas</span>
                  </div>
                </div>
                
                <div className="border-t pt-2">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Total a pagar:</span>
                    <div className="text-right">
                      <div className="flex items-center text-lg font-bold text-green-600 justify-end">
                        <DollarSign className="h-5 w-5" />
                        <span>{totalAmount.toFixed(2)}</span>
                      </div>
                      {isLoadingExchangeRate ? (
                        <p className="text-xs text-gray-500">Cargando tasa...</p>
                      ) : exchangeRate ? (
                        <p className="text-xs">
                          <strong>(Bs {(totalAmount * exchangeRate.usd_to_bs).toFixed(2)})</strong>
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
                
                {expectedReturnTime && (
                  <div className="border-t pt-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Hora de retorno:</span>
                      <span className="font-medium">
                        {expectedReturnTime.toLocaleTimeString('es-ES', {
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: true
                        })}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default PlanSelectionForm;
